import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import AdminInvitations from './AdminInvitations';
import PrivacyLegalSection from '../consent/PrivacyLegalSection';
import { db } from '../../config/firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

interface ApiKeyData {
  configured: boolean;
  lastUpdated?: any;
}

interface ApiKeysResponse {
  keys: Record<string, ApiKeyData>;
  updatedBy?: string;
  lastUpdated?: any;
}

type TabType = 'organisation' | 'users' | 'api' | 'billing' | 'customisation' | 'notifications' | 'security' | 'data';

const Settings: React.FC = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('organisation');

  // API & Integrations state (preserving existing functionality)
  const [apiKey, setApiKey] = useState('');
  const [keyName, setKeyName] = useState('mainApiKey');
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState<ApiKeysResponse>({ keys: {} });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Organisation state
  const [orgName, setOrgName] = useState('Virtual Client');
  const [subdomain, setSubdomain] = useState('');
  const [language, setLanguage] = useState('en-US');
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('12h');

  // Notifications state
  const [notifyNewManager, setNotifyNewManager] = useState(true);
  const [notifyBilling, setNotifyBilling] = useState(true);
  const [notifyFeatures, setNotifyFeatures] = useState(false);

  // Security state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // Org save state
  const [orgSaving, setOrgSaving] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Customisation state
  const [showPoweredBy, setShowPoweredBy] = useState(true);
  const [tagline, setTagline] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' ||
             (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  const fetchApiKeys = async () => {
    try {
      const token = currentUser ? await currentUser.getIdToken() : null;
      const response = await fetch(`${API_BASE_URL}/api/admin/get-api-keys`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'api' && currentUser) {
      fetchApiKeys();
    }
  }, [activeTab, currentUser]);

  // Load org settings from Firestore on mount
  useEffect(() => {
    const loadOrgSettings = async () => {
      try {
        const snap = await getDoc(doc(db, 'admin-settings', 'platform-config'));
        if (snap.exists()) {
          const data = snap.data();
          if (data.orgName) setOrgName(data.orgName);
          if (data.subdomain) setSubdomain(data.subdomain);
          if (data.language) setLanguage(data.language);
          if (data.timeFormat) setTimeFormat(data.timeFormat);
        }
      } catch {
        // silently ignore — defaults remain
      }
    };
    loadOrgSettings();
  }, []);

  // Dark mode effect
  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = (enabled: boolean) => {
    setDarkMode(enabled);
  };

  const handleSaveOrg = async () => {
    setOrgSaving(true);
    setMessage(null);
    try {
      await setDoc(doc(db, 'admin-settings', 'platform-config'), {
        orgName,
        subdomain,
        language,
        timeFormat,
        updatedBy: currentUser?.uid,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setMessage({ type: 'success', text: 'Organisation settings saved.' });
    } catch (err: any) {
      console.error('Save org error:', err);
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setOrgSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await setDoc(doc(db, 'admin-settings', 'deletion-request'), {
        status: 'pending_deletion',
        requestedBy: currentUser?.uid,
        requestedAt: serverTimestamp(),
        gracePeriodEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      }, { merge: true });
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      setMessage({ type: 'success', text: 'Deletion request submitted. Account will be deleted after a 30-day grace period.' });
    } catch (err: any) {
      console.error('Delete account error:', err);
      setDeleteError('Failed to submit deletion request. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSetApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      setMessage({ type: 'error', text: 'API key is required' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = currentUser ? await currentUser.getIdToken() : null;
      const response = await fetch(`${API_BASE_URL}/api/admin/set-api-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ apiKey, keyName })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
        setApiKey('');
        fetchApiKeys();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to set API key' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Never';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return 'Unknown';
    }
  };

  const tabs = [
    { id: 'organisation' as TabType, label: 'Organisation' },
    { id: 'users' as TabType, label: 'Users & Roles' },
    { id: 'api' as TabType, label: 'API & Integrations' },
    { id: 'billing' as TabType, label: 'Billing' },
    { id: 'customisation' as TabType, label: 'Customisation' },
    { id: 'notifications' as TabType, label: 'Notifications' },
    { id: 'security' as TabType, label: 'Security' },
    { id: 'data' as TabType, label: 'Data & Privacy' }
  ];

  const getTabLabel = (tabId: TabType) => {
    return tabs.find(t => t.id === tabId)?.label || '';
  };

  const ToggleSwitch = ({ enabled, onChange, label }: { enabled: boolean; onChange: (value: boolean) => void; label?: string }) => (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label || 'Toggle'}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-slate-900 dark:bg-slate-100' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-900 transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  return (
    <div className="flex-1 h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex flex-col transition-colors duration-200 relative overflow-hidden">
      {/* Dark mode accent elements */}
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-200">
        <div className="absolute top-20 left-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-500/3 rounded-full blur-3xl"></div>
      </div>
      <div className="flex-1 flex flex-col overflow-y-auto bg-transparent">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Settings
            </h1>
            <div className="flex items-center gap-1.5 text-xs mt-1">
              <span className="text-gray-500 dark:text-gray-400">Settings</span>
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-gray-900 dark:text-white font-semibold">{getTabLabel(activeTab)}</span>
            </div>
          </div>

          {/* Mobile tab select */}
          <div className="sm:hidden mb-4">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as TabType)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>{tab.label}</option>
              ))}
            </select>
          </div>

          {/* Tab Navigation */}
          <div className="mb-4 overflow-x-auto hidden sm:block">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav role="tablist" className="-mb-px flex space-x-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs transition-colors ${
                      activeTab === tab.id
                        ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-3">
            {/* Organisation Tab */}
            {activeTab === 'organisation' && (
              <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Organisation Settings</h2>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1.5">
                      Organisation Name
                    </label>
                    <input
                      type="text"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1.5">
                      Subdomain
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(e.g., kingstown.virtualy.ai)</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={subdomain}
                        onChange={(e) => setSubdomain(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400">.virtualy.ai</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      This defines the address your organisation uses to access Virtual Client.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1.5">
                      Language & Region
                    </label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                    >
                      <option value="en-US">English (US)</option>
                      <option value="en-GB">English (UK)</option>
                      <option value="es-ES">Spanish</option>
                      <option value="fr-FR">French</option>
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Default language and date format for all users.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1.5">
                      Date & Time Format
                    </label>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setTimeFormat('12h')}
                        className={`px-3 py-1.5 rounded-xl font-medium text-xs transition-all ${
                          timeFormat === '12h'
                            ? 'bg-blue-600 dark:bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        12 Hour
                      </button>
                      <button
                        onClick={() => setTimeFormat('24h')}
                        className={`px-3 py-1.5 rounded-xl font-medium text-xs transition-all ${
                          timeFormat === '24h'
                            ? 'bg-blue-600 dark:bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        24 Hour
                      </button>
                    </div>
                  </div>

                  {message && activeTab === 'organisation' && (
                    <p className={`text-xs ${message.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>{message.text}</p>
                  )}
                  <div className="pt-4">
                    <button
                      onClick={handleSaveOrg}
                      disabled={orgSaving}
                      className="px-4 py-2 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-semibold text-xs transition-all duration-200 disabled:opacity-60"
                    >
                      {orgSaving ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Users & Roles Tab */}
            {activeTab === 'users' && (
              <AdminInvitations />
            )}

            {/* API & Integrations Tab */}
            {activeTab === 'api' && (
              <div className="space-y-3">
                {/* OpenAI API Key */}
                <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">OpenAI API Key</h2>
                  </div>

                  {/* Current API Keys Status */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Current API Keys</h3>
                    {Object.keys(apiKeys.keys).length > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(apiKeys.keys).map(([name, data]) => (
                          <div key={name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-2xl">
                            <div>
                              <span className="font-medium text-gray-900 dark:text-white">{name}</span>
                              <span className="ml-2 px-2.5 py-0.5 text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                                Configured
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Last updated: {formatDate(data.lastUpdated)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4 text-sm">
                        No API keys configured yet.
                      </div>
                    )}
                  </div>

                  {/* Add/Update API Key Form */}
                  <form onSubmit={handleSetApiKey} className="space-y-4">
                    <div>
                      <label htmlFor="keyName" className="block text-xs font-medium text-gray-900 dark:text-white mb-1.5">
                        Key Name
                      </label>
                      <select
                        id="keyName"
                        value={keyName}
                        onChange={(e) => setKeyName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                      >
                        <option value="mainApiKey">Main API Key</option>
                        <option value="secondaryApiKey">Secondary API Key</option>
                        <option value="testApiKey">Test API Key</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="apiKey" className="block text-xs font-medium text-gray-900 dark:text-white mb-1.5">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKey ? 'text' : 'password'}
                          id="apiKey"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder="Enter your OpenAI API key"
                          className="w-full px-4 py-2.5 pr-10 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          {showApiKey ? (
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Enter your OpenAI API key to activate AI features.
                        <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                          Get your API key
                        </a>
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 py-2.5 px-4 rounded-2xl hover:bg-gray-800 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold"
                    >
                      {loading ? 'Setting API Key...' : 'Set API Key'}
                    </button>
                  </form>

                  {/* Status Messages */}
                  {message && (
                    <div className={`mt-4 p-4 rounded-2xl ${
                      message.type === 'success'
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800'
                    }`}>
                      {message.text}
                    </div>
                  )}
                </div>

                {/* Usage Overview */}
                <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Usage Overview</h2>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Requests this month:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">18,240</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Estimated tokens used:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">21.6K</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                    Displayed for reference only — will sync with OpenAI API in future updates.
                  </p>
                </div>

                {/* Additional Integrations */}
                <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                      </svg>
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Additional Integrations</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {['Simli', 'NavTalk', 'Google Analytics', 'Email Service'].map((service) => (
                      <div key={service} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{service}</span>
                        <span className="px-3 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full">
                          Coming Soon
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Billing Tab */}
            {activeTab === 'billing' && (
              <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Billing Overview</h2>
                </div>

                <div className="space-y-3">
                  {/* Current Plan */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Plan</h3>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Professional Plan</p>
                  </div>

                  {/* Next Billing Date */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Next Billing Date</h3>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">March 15, 2025</p>
                  </div>

                  {/* Usage Summary */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Usage Summary</h3>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Manager seats</span>
                        <span className="font-semibold text-gray-900 dark:text-white">2 of 5 used</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Payment Method</h3>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-2xl">
                      <svg className="w-8 h-5" viewBox="0 0 32 20" fill="none">
                        <rect width="32" height="20" rx="3" fill="#1434CB"/>
                        <rect x="8" y="6" width="16" height="8" rx="1" fill="#fff"/>
                      </svg>
                      <span className="text-sm text-gray-900 dark:text-white">•••• 4242</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-4 space-y-3">
                    <button
                      onClick={() => document.getElementById('plans-section')?.scrollIntoView({ behavior: 'smooth' })}
                      className="w-full px-4 py-2 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-semibold text-xs transition-all duration-200"
                    >
                      Upgrade Plan
                    </button>
                    <button
                      onClick={() => console.log('Cancel subscription')}
                      className="w-full px-6 py-2.5 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-900 dark:text-white rounded-2xl font-semibold text-sm transition-all duration-200"
                    >
                      Cancel Subscription
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Upgrading will open the Plans & Pricing page where you can explore available tiers. All plans are visual only in Phase 3. Stripe billing integration will be activated in a future update.
                  </p>
                </div>
              </div>
            )}

            {/* Customisation Tab */}
            {activeTab === 'customisation' && (
              <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-pink-600 dark:text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Customisation</h2>
                </div>

                <div className="space-y-3">
                  {/* Logo Upload */}
                  <div>
                    <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1.5">
                      Organisation Logo
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <button className="px-4 py-2 bg-gray-50 dark:bg-gray-700/30 text-gray-700 dark:text-gray-300 rounded-2xl font-medium text-sm hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors border border-gray-200 dark:border-gray-700">
                        Upload Logo
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Use a horizontal version of your logo for best results (recommended 300×80 px).
                    </p>
                  </div>

                  {/* Sidebar Color */}
                  <div>
                    <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1.5">
                      Sidebar Colour
                    </label>
                    <input
                      type="color"
                      defaultValue="#1f2937"
                      className="h-10 w-20 rounded-2xl cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      This changes sidebar and button accents across all dashboards. Choose a colour that keeps text legible.
                    </p>
                  </div>

                  {/* Tagline */}
                  <div>
                    <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1.5">
                      Tagline
                    </label>
                    <input
                      type="text"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      placeholder="Enter your organisation tagline"
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Dark Mode Toggle */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Dark Mode</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Switch between light and dark theme</p>
                    </div>
                    <ToggleSwitch enabled={darkMode} onChange={toggleDarkMode} />
                  </div>

                  {/* Powered By Toggle */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Show "Powered by Virtual Client"</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Display branding in footer</p>
                    </div>
                    <ToggleSwitch enabled={showPoweredBy} onChange={setShowPoweredBy} />
                  </div>

                  <div className="pt-4">
                    <button className="px-4 py-2 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-semibold text-xs transition-all duration-200">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Email Notifications</h2>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">New Manager Sign-ups</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Notify when new Manager signs up</p>
                    </div>
                    <ToggleSwitch enabled={notifyNewManager} onChange={setNotifyNewManager} />
                  </div>

                  <div className="flex items-center justify-between py-3 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Billing Changes</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Notify on billing changes</p>
                    </div>
                    <ToggleSwitch enabled={notifyBilling} onChange={setNotifyBilling} />
                  </div>

                  <div className="flex items-center justify-between py-3 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Feature Updates</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Receive new feature updates</p>
                    </div>
                    <ToggleSwitch enabled={notifyFeatures} onChange={setNotifyFeatures} />
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Security Settings</h2>
                </div>

                <div className="space-y-3">
                  {/* Reset Password */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-1.5">Password</h3>
                    <button className="px-4 py-2 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-semibold text-xs transition-all duration-200">
                      Reset Password
                    </button>
                  </div>

                  {/* 2FA Toggle */}
                  <div className="flex items-center justify-between py-4 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Two-Factor Authentication (2FA)</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Adds an extra verification step at login to secure your account.
                      </p>
                    </div>
                    <ToggleSwitch enabled={twoFactorEnabled} onChange={setTwoFactorEnabled} />
                  </div>

                  {/* Last Login */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-1.5">Last Login</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">December 27, 2025 at 10:30 AM</p>
                    <button type="button" className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                      View Session History
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Data & Privacy Tab */}
            {activeTab === 'data' && (
              <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Data & Privacy</h2>
                </div>

                <div className="space-y-3">
                  {/* Download Data */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-1.5">Export Organisation Data</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      Download all your organisation's data in CSV format.
                    </p>
                    <button className="px-4 py-2 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-semibold text-xs transition-all duration-200 inline-flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Data (CSV)
                    </button>
                  </div>

                  {/* Delete Account */}
                  <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">Delete Organisation Account</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      Permanently removes all organisation data. This action cannot be undone.
                    </p>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white rounded-xl font-semibold text-xs transition-all duration-200"
                    >
                      Delete Account
                    </button>
                  </div>

                </div>
              </div>
            )}

            {activeTab === 'data' && (
              <div className="mt-4">
                <PrivacyLegalSection />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-red-600 dark:text-red-400 mb-2">Delete Organisation Account</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This will schedule your organisation account for permanent deletion after a <strong>30-day grace period</strong>. All data will be removed. This action cannot be undone.
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Type <span className="font-mono text-red-600">DELETE</span> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            {deleteError && (
              <p className="text-xs text-red-600 dark:text-red-400 mb-3">{deleteError}</p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); setDeleteError(''); }}
                className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                className="px-4 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all"
              >
                {deleteLoading ? 'Submitting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
